<script setup lang="ts">
import { EHubProvider } from '@/types/enum/HubProviderEnum'
import type { EventModel } from '@/types/models/EventModel'
import EventPartnerConfig from '@/views/EventsView/Detail/components/EventPartnerConfig.vue'
import {
    StackLayout,
    TitleTypography,
    TypographyItem,
    TypographyWithPrefix
} from '@rfc/rfc-components-library'
import { computed, inject } from 'vue'
import type { PartnerConfigModel } from '../../../../types/models/PartnerConfigModel'
import EditEventInformations from './EditEventInformations.vue'

type Props = {
    data: EventModel
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'updatePartnerConfig', value: Partial<PartnerConfigModel>): void
}
defineEmits<Emits>()

const partnerConfig = computed(() =>
    props.data?.partner?.config?.event ? props.data.partner?.config?.event : undefined
)
const editPartnerConfig = computed(() =>
    props.data?.partner_config ? props.data.partner_config : undefined
)

const isViewerOnly = inject<boolean>(EHubProvider.IS_VIEWER_ONLY, false)
</script>

<template>
    <StackLayout class="section-box" direction="column">
        <StackLayout class="section-header">
            <TypographyItem
                :label="$t('event.detail.informations.informationsTitle')"
                class="section-text" />
            <EditEventInformations
                v-if="!isViewerOnly"
                :eventId="data.id"
                :data="data"
                :partnerConfig="partnerConfig"
                :partnerConfigValues="editPartnerConfig"
                @refresh="$emit('refresh')" />
        </StackLayout>
        <StackLayout class="px-7 pt-4 pb-5" direction="column">
            <TitleTypography
                :title="data?.name"
                class="title underline decoration-orange-500 underline-offset-8 section-text w-fit pb-1" />
            <TypographyWithPrefix
                :prefix="$t('event.model.eventId')"
                :label="data?.id?.toString()"
                boldLabel />
            <TypographyWithPrefix
                v-if="data?.partner"
                :prefix="$t('event.model.currentPartner')"
                :label="data?.partner?.name"
                boldLabel />
            <EventPartnerConfig
                v-if="editPartnerConfig"
                :partnerConfig="partnerConfig"
                :partnerConfigValues="editPartnerConfig"
                boldLabel
                @update="$emit('updatePartnerConfig', $event)" />
        </StackLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.section-box {
    & :deep(.title) {
        font-size: 1.4em;
    }
}
</style>
